import React, { useState, useEffect } from 'react';
import { Home } from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import { Sidebar, Menu, MenuItem } from 'react-pro-sidebar';
import CreateIcon from '@mui/icons-material/Create';
import ImageIcon from '@mui/icons-material/Image';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import WindowIcon from '@mui/icons-material/Window';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import styles from './Menu.module.css'
import InvoiceSummary from '../Invoice Summary/InvoiceSummary';
import { useNavigate, useLocation } from 'react-router-dom';
import useScreenSize from '../useScreenSize';
import { useSubscription, useMutation, useQuery } from '@apollo/client';
import { GENERATE_UPLOAD_URL, GET_MY_PROFILE, INVOICE_UPDATED_SUBSCRIPTION, USER_UPDATED_SUBSCRIPTION } from '../../services/graphql';
import { MarkChatRead } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';
import keycloak from '../../keycloak';

const MenuWeb = ({ collapsedMobile = false, onLogout }) => {
  const PAYMENT_DETAILS_REQUIRED_MESSAGE = 'Update your payment details in My Profile section to be able to submit invoices.';
  const [collapsed, setCollapsed] = useState(collapsedMobile);
  const navigate = useNavigate();
  const location = useLocation();
  const screenSize = useScreenSize();

  const accessLevel = useSelector(state => state.graphql.accessLevel);
  const currentUserEmail = useSelector(state => state.graphql.email);
  const resolvedCurrentUserEmail =
    currentUserEmail || keycloak?.tokenParsed?.email || sessionStorage.getItem('email') || null;
  const { data: profileData, loading: profileLoading } = useQuery(GET_MY_PROFILE, {
    skip: !resolvedCurrentUserEmail,
    variables: { email: resolvedCurrentUserEmail },
  });
  //console.log("Menu.js access level", accessLevel);
  const isApprover = accessLevel?.startsWith('Approver');
  const isManager = accessLevel === 'Manager';
  const profileBody = profileData?.getUserProfile?.body;
  const hasInteracDetails = Boolean((profileBody?.interacId || '').trim() && (profileBody?.securityAnswer || '').trim());
  const hasBankTransferDetails = Boolean((profileBody?.voidChequeFilename || '').trim());
  const hasPaymentDetails = hasInteracDetails || hasBankTransferDetails;
  const isSubmitInvoiceDisabled = !profileLoading && Boolean(profileBody) && !hasPaymentDetails; //avoids a false disable for Submit Invoice while GET_MY_PROFILE is still loading

  const invoiceNotification = useSubscription(INVOICE_UPDATED_SUBSCRIPTION);
  const userNotification = useSubscription(USER_UPDATED_SUBSCRIPTION);

  const { enqueueSnackbar } = useSnackbar();

  const [upload] = useMutation(GENERATE_UPLOAD_URL, {
    onCompleted: (data) => {
      console.log(data);
      localStorage.setItem('uploadUrl', data.generateUploadUrl.body);
    },
    onError: () => {
      alert("error.")
    },
  });

  var isMobile = screenSize.width < 600 ? true : false;

  const handleToggleSidebar = () => {
    setCollapsed(!collapsed);
  };

  useEffect(() => {
    if (!invoiceNotification.data) return; // Early return if the condition is not met
    // refresh the page if you are on "approve-invoices" page
    if (location.pathname === '/approve-invoices') {
      window.location.reload();
    }
    else{
      enqueueSnackbar(`Invoice Update: ${invoiceNotification.data?.invoiceUpdated?.message}`, { variant: 'info' });
    }
  }, [invoiceNotification.data, enqueueSnackbar]);

  useEffect(() => {
    // console.log("data :: " , data?.invoiceUpdated?.message);
    if (!userNotification.data) return; // Early return if the condition is not met
      // You can adjust this to use different parts of the userNotification.data for different messages or types
    if(location.pathname === '/approve-users'){
      window.location.reload();
    }
    else{
      enqueueSnackbar(`User Update: ${userNotification.data?.userUpdated?.message}`, { variant: 'info' });
    }
  }, [userNotification.data, enqueueSnackbar]);


  useEffect(() => {
    setCollapsed(collapsedMobile);
  }, [collapsedMobile]);

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      setCollapsed(true);
    }
  };

  if (isMobile && collapsed) {
    return null;
  }



  return (
    <Sidebar backgroundColor="" className={styles.sidebar} collapsed={collapsed}>
      <Menu
        className={styles.menuRoot}
        menuItemStyles={{
        button: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      }}>
        {!isMobile && <MenuItem className={styles.item} icon={<MenuIcon />} onClick={handleToggleSidebar}></MenuItem>}
        <MenuItem
          className={`${styles.item} ${location.pathname === '/' ? styles.selected : ''}`}
          icon={<Home />}
          onClick={() => handleMenuItemClick("/")}
        >
          Dashboard
        </MenuItem>

        <MenuItem
          className={`${styles.item} ${location.pathname === '/submit-invoice' ? (styles.selected) : ''} ${isSubmitInvoiceDisabled ? styles.submitInvoiceDisabled : ''}`}
          icon={<CreateIcon />}
          onClick={() => {
            if (isSubmitInvoiceDisabled) {
              alert(PAYMENT_DETAILS_REQUIRED_MESSAGE);
              return;
            }
            handleMenuItemClick("/submit-invoice");
            upload();
          }}
        >
          Submit an Invoice
        </MenuItem>

        {isApprover ? <MenuItem
          className={`${styles.item} ${location.pathname === '/approve-invoices' ? styles.selected : ''}`}
          icon={<MarkChatRead />}
          onClick={() => handleMenuItemClick("/approve-invoices")}
        >
          Approve Invoices
        </MenuItem> : null}

        {/* TODO: Verify Approval Tier  */}
        {isApprover ? <MenuItem
          className={`${styles.item} ${location.pathname === '/approve-users' ? styles.selected : ''}`}
          icon={<TextSnippetIcon />}
          onClick={() => handleMenuItemClick("/approve-users")}
        >
          Approve Users
        </MenuItem> : null}

        {isManager ? <MenuItem
          className={`${styles.item} ${location.pathname === '/create-user' ? styles.selected : ''}`}
          icon={<ImageIcon />}
          onClick={() => handleMenuItemClick("/create-user")}
        >
          Create a new user
        </MenuItem> : null}

        {isManager ? <MenuItem
          className={`${styles.item} ${location.pathname === '/approval-settings' ? styles.selected : ''}`}
          icon={<TextSnippetIcon />}
          onClick={() => handleMenuItemClick("/approval-settings")}
        >
          Approval Settings
        </MenuItem> : null}

        {isManager ? <MenuItem
          className={`${styles.item} ${location.pathname === '/company-overview' ? styles.selected : ''}`}
          icon={<WindowIcon />}
          onClick={() => handleMenuItemClick("/company-overview")}
        >
          Company Overview
        </MenuItem> : null}

        {isManager ? <MenuItem
          className={`${styles.item} ${location.pathname === '/user-management' ? styles.selected : ''}`}
          icon={<PeopleAltIcon />}
          onClick={() => handleMenuItemClick("/user-management")}
        >
          User Management
        </MenuItem> : null}

        <MenuItem
          className={`${styles.item} ${location.pathname === '/my-profile' ? styles.selected : ''}`}
          icon={<PersonIcon />}
          onClick={() => handleMenuItemClick("/my-profile")}
        >
          My Profile
        </MenuItem>

        <MenuItem
          className={`${styles.item} ${location.pathname === '/settings' ? styles.selected : ''}`}
          icon={<SettingsIcon />}
          onClick={() => handleMenuItemClick("/settings")}
        >
          Settings
        </MenuItem>

        <div className={styles.bottomItems}>
          {!collapsed && <InvoiceSummary></InvoiceSummary>}
          <MenuItem
            className={`${styles.logout} ${location.pathname === '/logout' ? styles.selected : ''}`}
            icon={<LogoutIcon />}
            onClick={onLogout}
          >
            {!collapsed && "Logout"}
          </MenuItem>
        </div>
      </Menu>

    </Sidebar>
  );
};

export default MenuWeb;
